import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Camera } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PHOTO_BRIEFS } from "../data/photoBriefs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: "pressure-washing",    name: "Pressure Washing" },
  { id: "awning-cleaning",     name: "Awning Cleaning" },
  { id: "window-cleaning",     name: "Window Cleaning" },
  { id: "sidewalk-cleaning",   name: "Sidewalk Cleaning" },
  { id: "parking-lot-striping",name: "Parking Lot Striping" },
  { id: "parking-lot-sweeping",name: "Parking Lot Sweeping" },
  { id: "snow-removal",        name: "Snow Removal" },
  { id: "graffiti-removal",    name: "Graffiti Removal" },
  { id: "dumpster-cleaning",   name: "Dumpster Pad Cleaning" },
  { id: "outdoor-lighting",    name: "Permanent Outdoor Lighting" },
  { id: "pest-control",        name: "Commercial Pest Control" },
];

export default function MediaKitTab() {
  const [media, setMedia] = useState({});
  const [busyId, setBusyId] = useState(null);
  const fileRefs = useRef({});

  async function load() {
    const r = await axios.get(`${API}/media/services`);
    const raw = r.data.media || {};
    const base = process.env.REACT_APP_BACKEND_URL;
    const absolute = {};
    for (const [k, v] of Object.entries(raw)) {
      absolute[k] = v.startsWith("http") ? v : `${base}${v}`;
    }
    setMedia(absolute);
  }
  useEffect(() => { load(); }, []);

  async function upload(sid, file) {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/avif"].includes(file.type)) {
      toast.error("JPG, PNG, WEBP, or AVIF only."); return;
    }
    if (file.size > 12 * 1024 * 1024) { toast.error("Max 12 MB."); return; }
    setBusyId(sid);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/media/service/${sid}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Photo saved for ${SERVICES.find((s) => s.id === sid)?.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusyId(null); }
  }

  async function remove(sid) {
    if (!window.confirm("Remove this photo and revert to the placeholder?")) return;
    setBusyId(sid);
    try {
      await axios.delete(`${API}/admin/media/service/${sid}`);
      toast.success("Reverted to placeholder");
      await load();
    } catch {
      toast.error("Delete failed");
    } finally { setBusyId(null); }
  }

  return (
    <div data-testid="media-kit-tab">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--gold)" }}>Media Kit</div>
        <h2 className="font-display text-4xl md:text-5xl mt-2" style={{ letterSpacing: "-0.03em" }}>
          Service Photography
        </h2>
        <p className="mt-4 font-body max-w-2xl" style={{ color: "var(--text-2)" }}>
          Drop a licensed photo into any service to instantly replace the Photography Brief placeholder site-wide.
          JPG / PNG / WEBP / AVIF · Max 12 MB · Recommended 1400×1000 or larger.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((s) => {
          const url = media[s.id];
          const brief = PHOTO_BRIEFS[s.id];
          const busy = busyId === s.id;
          return (
            <div
              key={s.id}
              className="glass-card p-6"
              data-testid={`media-tile-${s.id}`}
            >
              <div className="aspect-[16/10] w-full overflow-hidden mb-5 relative flex items-center justify-center" style={{ borderRadius: "var(--r-lg)", background: "var(--surface)", border: "1px solid var(--line)" }}>
                {url ? (
                  <img src={url} alt={s.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center px-4">
                    <Camera size={22} style={{ color: "var(--gold)" }} strokeWidth={1.3} className="mx-auto mb-3" />
                    <div className="font-mono text-[9px] uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>Placeholder</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.24em] mt-2" style={{ color: "var(--text-3)" }}>Awaiting photo</div>
                  </div>
                )}
              </div>

              <div className="font-display text-2xl" style={{ letterSpacing: "-0.02em", color: "var(--text)" }}>{s.name}</div>
              {brief && (
                <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)", lineHeight: 1.6 }}>
                  {brief.subject}
                </div>
              )}

              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                className="hidden"
                data-testid={`media-file-${s.id}`}
                ref={(el) => (fileRefs.current[s.id] = el)}
                onChange={(e) => upload(s.id, e.target.files?.[0])}
              />
              <div className="mt-5 flex gap-2">
                <button
                  onClick={() => fileRefs.current[s.id]?.click()}
                  disabled={busy}
                  data-testid={`media-upload-${s.id}`}
                  data-cursor="hover"
                  className="btn-ghost flex-1 justify-center"
                >
                  <Upload size={13} /> {busy ? "Uploading…" : url ? "Replace" : "Upload"}
                </button>
                {url && (
                  <button
                    onClick={() => remove(s.id)}
                    disabled={busy}
                    data-testid={`media-delete-${s.id}`}
                    className="w-11 h-11 flex items-center justify-center transition-colors"
                    style={{ border: "1px solid var(--line-2)", color: "var(--text-2)", borderRadius: "var(--r-full)" }}
                    aria-label="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
